import { apiFetch as fetch } from "../lib/security";
import { FormEvent, KeyboardEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, m } from "framer-motion";
import { easeOut } from "./motion/tokens";
import { ArrowRight, CalendarCheck, Check, Clock, CreditCard, LogIn, Mail, Search, ShieldCheck, UserRound, Users, X } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import type { AccountBooking, GuestAccount } from "../context/AuthContext";
import { getApartment } from "../data/apartments";

type BookingSearchProps = {
  compact?: boolean;
  apartmentSlug?: string;
  id?: string;
};

type ApiApartment = {
  slug: string;
  title: string;
  status: "available" | "non-bookable";
  maxGuests: number;
  priceNight: number | null;
  available?: boolean;
  reason?: string | null;
  nightlySubtotal?: number | null;
  cleaningFee?: number | null;
  total?: number | null;
};

type SearchResult = {
  start: string;
  end: string;
  nights: number;
  guests: number;
  apartments?: ApiApartment[];
  apartment?: ApiApartment;
  available?: boolean;
  reason?: string | null;
  nightlySubtotal?: number | null;
  cleaningFee?: number | null;
  total?: number | null;
};

type BookingResult = {
  booking: {
    id: string;
    reference?: string;
    start: string;
    end: string;
    nights: number;
    adults: number;
    children: number;
    status: "hold" | "confirmed" | "cancelled";
    holdExpiresAt?: string;
    nightlySubtotal?: number | null;
    cleaningFee?: number | null;
    total: number | null;
  };
  account?: GuestAccount | null;
  bookings?: AccountBooking[];
  sessionToken?: string;
  sessionExpiresAt?: string;
};

const apiBase = "/api";
const bookingTimeZone = "America/New_York";

function money(value: number | null | undefined) {
  if (typeof value !== "number") {
    return "Ask";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

function todayDate() {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: bookingTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

function addDays(dateOnly: string, days: number) {
  const date = new Date(`${dateOnly}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function isDateOnly(value: string | null) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }

  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === value;
}

function searchGuestValue(value: string | null, fallback: string, min: number) {
  const parsed = Number(value ?? fallback);

  if (!Number.isInteger(parsed) || parsed < min || parsed > 8) {
    return fallback;
  }

  return String(parsed);
}

function initialBookingValues(searchParams: URLSearchParams, minDate: string) {
  const queryStart = searchParams.get("start");
  const queryEnd = searchParams.get("end");
  const checkIn = typeof queryStart === "string" && isDateOnly(queryStart) && queryStart >= minDate ? queryStart : "";
  const checkOut = checkIn && typeof queryEnd === "string" && isDateOnly(queryEnd) && queryEnd > checkIn ? queryEnd : "";

  return {
    checkIn,
    checkOut,
    adults: searchGuestValue(searchParams.get("adults"), "2", 1),
    children: searchGuestValue(searchParams.get("children"), "0", 0),
  };
}

function formatStayDate(dateOnly: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${dateOnly}T00:00:00.000Z`));
}

function formatBookingDateTime(value: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: bookingTimeZone,
    timeZoneName: "short",
  }).format(new Date(value));
}

function openNativeDatePicker(input: HTMLInputElement) {
  const dateInput = input as HTMLInputElement & { showPicker?: () => void };

  dateInput.focus({ preventScroll: true });

  try {
    dateInput.showPicker?.();
  } catch {
    // Some browsers only allow showPicker during specific user gestures.
  }
}

function priceSummary(total: number | null | undefined, cleaningFee: number | null | undefined) {
  if (typeof total !== "number") {
    return "";
  }

  if (typeof cleaningFee !== "number") {
    return `estimated ${money(total)}`;
  }

  return `estimated ${money(total)} including ${money(cleaningFee)} cleaning fee`;
}

function isExistingAccountBookingError(message: string) {
  const normalized = message.toLowerCase();

  return normalized.includes("account already exists") && normalized.includes("sign in");
}

function BookingNextSteps() {
  return (
    <div className="booking-next-steps" aria-label="What happens after you request a stay">
      <div>
        <Clock size={17} aria-hidden="true" />
        <span>Dates are held while the owner reviews availability.</span>
      </div>
      <div>
        <CreditCard size={17} aria-hidden="true" />
        <span>No online payment is collected with this request.</span>
      </div>
      <div>
        <Mail size={17} aria-hidden="true" />
        <span>Confirmation and next steps arrive by email or phone.</span>
      </div>
    </div>
  );
}

export function BookingSearch({ compact = false, apartmentSlug, id }: BookingSearchProps) {
  const { account, sessionToken, applySession, refreshAccount, login } = useAuth();
  const [searchParams] = useSearchParams();
  const minDate = todayDate();
  const initialValues = initialBookingValues(searchParams, minDate);
  const [checkIn, setCheckIn] = useState(initialValues.checkIn);
  const [checkOut, setCheckOut] = useState(initialValues.checkOut);
  const [adults, setAdults] = useState(initialValues.adults);
  const [children, setChildren] = useState(initialValues.children);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [note, setNote] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState("");
  const [returningSignInOpen, setReturningSignInOpen] = useState(false);
  const [returningEmail, setReturningEmail] = useState("");
  const [returningPassword, setReturningPassword] = useState("");
  const [returningStatus, setReturningStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [returningMessage, setReturningMessage] = useState("");
  const [bookingStatus, setBookingStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [bookingMessage, setBookingMessage] = useState("");
  const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [autoSearchReady] = useState(Boolean(apartmentSlug && initialValues.checkIn && initialValues.checkOut));
  const checkoutMinDate = checkIn ? addDays(checkIn, 1) : minDate;

  useEffect(() => {
    if (!account) {
      return;
    }

    setGuestName((value) => value || account.name);
    setGuestEmail(account.email);
    setGuestPhone((value) => value || account.phone);
    setMarketingOptIn(account.marketingOptIn);
    setCreateAccount(false);
    setAccountPassword("");
    setAccountPasswordConfirm("");
    setReturningSignInOpen(false);
    setReturningPassword("");
    setReturningStatus("idle");
    setReturningMessage("");
  }, [account]);

  useEffect(() => {
    if (!account || bookingStatus !== "error" || !isExistingAccountBookingError(bookingMessage)) {
      return;
    }

    setBookingStatus("idle");
    setBookingMessage("");
  }, [account, bookingMessage, bookingStatus]);

  const clearAvailabilityState = () => {
    setStatus("idle");
    setMessage("");
    setResult(null);
    setBookingStatus("idle");
    setBookingMessage("");
    setBookingResult(null);
  };

  useEffect(() => {
    if (!availabilityModalOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        setAvailabilityModalOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [availabilityModalOpen]);

  const toggleReturningSignIn = () => {
    setReturningSignInOpen((open) => {
      const nextOpen = !open;

      if (nextOpen) {
        setCreateAccount(false);
        setAccountPassword("");
        setAccountPasswordConfirm("");
        setReturningEmail((value) => value || guestEmail);
        setReturningStatus("idle");
        setReturningMessage("");
      }

      return nextOpen;
    });
  };

  const onReturningLogin = async () => {
    const email = (returningEmail || guestEmail).trim();

    if (!email || !returningPassword) {
      setReturningStatus("error");
      setReturningMessage("Enter your account email and password.");
      return;
    }

    setReturningStatus("loading");
    setReturningMessage("");

    try {
      await login({ email, password: returningPassword });
      setReturningStatus("success");
      setReturningMessage("");
      setReturningPassword("");
      setReturningSignInOpen(false);
      setCreateAccount(false);
    } catch (error) {
      setReturningStatus("error");
      setReturningMessage(error instanceof Error ? error.message : "Unable to sign in.");
    }
  };

  const onReturningKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    void onReturningLogin();
  };

  const onCheckInChange = (value: string) => {
    clearAvailabilityState();
    setCheckIn(value);

    if (value && checkOut && checkOut <= value) {
      setCheckOut(addDays(value, 1));
    }
  };

  const onCheckOutChange = (value: string) => {
    clearAvailabilityState();

    if (value && value < checkoutMinDate) {
      setCheckOut(checkoutMinDate);
      return;
    }

    setCheckOut(value);
  };

  const onAdultsChange = (value: string) => {
    clearAvailabilityState();
    setAdults(value);
  };

  const onChildrenChange = (value: string) => {
    clearAvailabilityState();
    setChildren(value);
  };

  const checkAvailability = async ({
    nextCheckIn = checkIn,
    nextCheckOut = checkOut,
    nextAdults = adults,
    nextChildren = children,
  }: {
    nextCheckIn?: string;
    nextCheckOut?: string;
    nextAdults?: string;
    nextChildren?: string;
  } = {}) => {
    setStatus("loading");
    setMessage("");
    setResult(null);
    setBookingStatus("idle");
    setBookingMessage("");
    setBookingResult(null);

    if (!nextCheckIn || !nextCheckOut) {
      setStatus("error");
      setMessage("Please choose check-in and check-out dates.");
      return;
    }

    if (nextCheckIn < minDate) {
      setStatus("error");
      setMessage("Check-in cannot be in the past.");
      return;
    }

    if (nextCheckOut <= nextCheckIn) {
      setStatus("error");
      setMessage("Check-out must be after check-in.");
      return;
    }

    if (Number(nextAdults) + Number(nextChildren) < 1) {
      setStatus("error");
      setMessage("Please select at least one guest.");
      return;
    }

    const params = new URLSearchParams({
      start: nextCheckIn,
      end: nextCheckOut,
      adults: nextAdults,
      children: nextChildren,
    });

    if (apartmentSlug) {
      params.set("apartment", apartmentSlug);
    }

    try {
      const endpoint = apartmentSlug ? `${apiBase}/availability?${params.toString()}` : `${apiBase}/availability/search?${params.toString()}`;
      const response = await fetch(endpoint);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error?.message ?? "Unable to check availability");
      }

      setResult(data);
      setStatus("success");

      if (!apartmentSlug) {
        setAvailabilityModalOpen(true);
      }
    } catch (error) {
      setStatus("error");
      setMessage(error instanceof Error ? error.message : "Unable to check availability");
    }
  };

  useEffect(() => {
    if (!autoSearchReady) {
      return;
    }

    void checkAvailability({
      nextCheckIn: initialValues.checkIn,
      nextCheckOut: initialValues.checkOut,
      nextAdults: initialValues.adults,
      nextChildren: initialValues.children,
    });
  }, []);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await checkAvailability();
  };

  const onBookingSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!apartmentSlug || !result?.available) {
      return;
    }

    setBookingStatus("loading");
    setBookingMessage("");
    setBookingResult(null);

    if (!account && createAccount) {
      if (accountPassword.length < 8) {
        setBookingStatus("error");
        setBookingMessage("Password must be at least 8 characters.");
        return;
      }

      if (accountPassword !== accountPasswordConfirm) {
        setBookingStatus("error");
        setBookingMessage("Passwords do not match.");
        return;
      }
    }

    try {
      const headers = new Headers({ "Content-Type": "application/json" });

      if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
      }

      const response = await fetch(`${apiBase}/bookings`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          apartment: apartmentSlug,
          start: result.start,
          end: result.end,
          adults: Number(adults),
          children: Number(children),
          guestName,
          guestEmail,
          guestPhone,
          note,
          marketingOptIn,
          createAccount: !account && createAccount,
          accountPassword: !account && createAccount ? accountPassword : undefined,
        }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error?.message ?? "Unable to create booking request");
      }

      setBookingResult(data);
      setBookingStatus("success");
      setAccountPassword("");
      setAccountPasswordConfirm("");

      if (data?.account) {
        applySession({
          account: data.account,
          bookings: data.bookings ?? [],
          sessionToken: data.sessionToken,
          sessionExpiresAt: data.sessionExpiresAt,
        });
      } else if (sessionToken) {
        void refreshAccount();
      }
    } catch (error) {
      const nextMessage = error instanceof Error ? error.message : "Unable to create booking request";
      setBookingStatus("error");
      setBookingMessage(nextMessage);

      if (!account && isExistingAccountBookingError(nextMessage)) {
        setCreateAccount(false);
        setAccountPassword("");
        setAccountPasswordConfirm("");
        setReturningSignInOpen(true);
        setReturningEmail((guestEmail || returningEmail).trim());
        setReturningPassword("");
        setReturningStatus("idle");
        setReturningMessage("");
      }
    }
  };

  const availableApartments = result?.apartments?.filter((apartment) => apartment.available) ?? [];
  const searchedApartments = result?.apartments ?? [];
  const selectedApartmentDetails = apartmentSlug ? getApartment(apartmentSlug) : undefined;
  const bookingHoldExpiresText = bookingResult?.booking.holdExpiresAt ? formatBookingDateTime(bookingResult.booking.holdExpiresAt) : "";
  const bookingReference = bookingResult?.booking.reference || bookingResult?.booking.id.slice(0, 8).toUpperCase() || "";
  const bookingNeedsSignIn = bookingStatus === "error" && isExistingAccountBookingError(bookingMessage);

  return (
    <div className={compact ? "booking-widget booking-widget--compact" : "booking-widget"} id={id}>
      <form className={compact ? "booking-form booking-form--compact" : "booking-form"} onSubmit={onSubmit}>
        <label>
          <span>Check-in</span>
          <input
            name="checkIn"
            type="date"
            min={minDate}
            value={checkIn}
            onClick={(event) => openNativeDatePicker(event.currentTarget)}
            onChange={(event) => onCheckInChange(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Check-out</span>
          <input
            name="checkOut"
            type="date"
            min={checkoutMinDate}
            value={checkOut}
            onClick={(event) => openNativeDatePicker(event.currentTarget)}
            onChange={(event) => onCheckOutChange(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Adults</span>
          <select name="adults" value={adults} onChange={(event) => onAdultsChange(event.target.value)}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Children</span>
          <select name="children" value={children} onChange={(event) => onChildrenChange(event.target.value)}>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <button className="button button--primary" type="submit" disabled={status === "loading"}>
          <Search size={18} aria-hidden="true" />
          {status === "loading" ? "Checking" : "Search"}
        </button>
      </form>

      {status === "error" && (
        <div className="booking-results booking-results--error" role="status" aria-live="polite">
          <X size={18} aria-hidden="true" />
          <span>{message}</span>
        </div>
      )}

      {status === "success" && result && apartmentSlug && (
        <div className={result.available ? "booking-results booking-results--success" : "booking-results booking-results--error"} role="status" aria-live="polite">
          {result.available ? <Check size={18} aria-hidden="true" /> : <X size={18} aria-hidden="true" />}
          <span>
            {result.available
              ? `Available for ${result.nights} night${result.nights === 1 ? "" : "s"}${result.total ? `, ${priceSummary(result.total, result.cleaningFee)}` : ""}.`
              : result.reason ?? "Unavailable for those dates."}
          </span>
        </div>
      )}

      {status === "success" && result?.available && apartmentSlug && bookingStatus !== "success" && (
        <form className="booking-request-form" onSubmit={onBookingSubmit}>
          {selectedApartmentDetails && (
            <div className="booking-selected-stay booking-request-form__full">
              <img src={selectedApartmentDetails.featuredImage} alt={selectedApartmentDetails.alt} />
              <div>
                <span>{selectedApartmentDetails.category}</span>
                <strong>{selectedApartmentDetails.detailTitle ?? selectedApartmentDetails.title}</strong>
                <p>
                  {formatStayDate(result.start)} to {formatStayDate(result.end)} · {result.nights} night{result.nights === 1 ? "" : "s"} · {result.guests} guest
                  {result.guests === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          )}
          <div className="booking-request-form__intro">
            <strong>Complete your booking request</strong>
            <span>
              {formatStayDate(result.start)} to {formatStayDate(result.end)} · {result.nights} night{result.nights === 1 ? "" : "s"} ·{" "}
              {result.guests} guest{result.guests === 1 ? "" : "s"}
            </span>
          </div>
          {!account && (
            <div className="booking-returning-guest">
              <div className="booking-returning-guest__summary">
                <UserRound size={18} aria-hidden="true" />
                <span>
                  <strong>Returning guest</strong>
                  <small>Sign in so this request lands in your saved stays.</small>
                </span>
              </div>
              <button className="booking-inline-button" type="button" onClick={toggleReturningSignIn} aria-expanded={returningSignInOpen}>
                <LogIn size={16} aria-hidden="true" />
                {returningSignInOpen ? "Close" : "Sign in"}
              </button>
              {returningSignInOpen && (
                <div className="booking-returning-guest__fields">
                  <label>
                    <span>Email</span>
                    <input
                      name="returningEmail"
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      value={returningEmail}
                      onChange={(event) => setReturningEmail(event.target.value)}
                      onKeyDown={onReturningKeyDown}
                    />
                  </label>
                  <label>
                    <span>Password</span>
                    <input
                      name="returningPassword"
                      type="password"
                      autoComplete="current-password"
                      maxLength={128}
                      value={returningPassword}
                      onChange={(event) => setReturningPassword(event.target.value)}
                      onKeyDown={onReturningKeyDown}
                    />
                  </label>
                  <button className="button button--light button--full" type="button" onClick={() => void onReturningLogin()} disabled={returningStatus === "loading"}>
                    {returningStatus === "loading" ? "Signing in" : "Sign in"}
                  </button>
                </div>
              )}
              {returningStatus === "error" && <p className="booking-returning-guest__message">{returningMessage}</p>}
            </div>
          )}
          {account && (
            <div className="booking-account-banner">
              <UserRound size={18} aria-hidden="true" />
              <span>Signed in as {account.email}</span>
            </div>
          )}
          {typeof result.total === "number" && (
            <div className="booking-price-breakdown">
              <span>
                <span>Nightly subtotal</span>
                <strong>{money(result.nightlySubtotal)}</strong>
              </span>
              <span>
                <span>Cleaning fee</span>
                <strong>{money(result.cleaningFee)}</strong>
              </span>
              <span>
                <span>Estimated total</span>
                <strong>{money(result.total)}</strong>
              </span>
            </div>
          )}
          <label>
            <span>Name</span>
            <input
              name="guestName"
              type="text"
              autoComplete="name"
              maxLength={120}
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Email</span>
            <input
              name="guestEmail"
              type="email"
              autoComplete="email"
              maxLength={254}
              value={guestEmail}
              onChange={(event) => setGuestEmail(event.target.value)}
              readOnly={Boolean(account)}
              required
            />
          </label>
          <label>
            <span>Phone</span>
            <input
              name="guestPhone"
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              maxLength={40}
              value={guestPhone}
              onChange={(event) => setGuestPhone(event.target.value)}
              required
            />
          </label>
          <label className="booking-checkbox booking-request-form__full">
            <input type="checkbox" checked={marketingOptIn} onChange={(event) => setMarketingOptIn(event.target.checked)} />
            <span>
              <strong>Email me discounts and stay updates</strong>
              <small>Seasonal rates, openings, and booking reminders.</small>
            </span>
          </label>
          {!account && (
            <label className="booking-checkbox booking-request-form__full">
              <input
                type="checkbox"
                checked={createAccount}
                onChange={(event) => {
                  setCreateAccount(event.target.checked);
                  setAccountPassword("");
                  setAccountPasswordConfirm("");
                }}
              />
              <span>
                <strong>Save these details in a guest account</strong>
                <small>Use this email and phone for future stays.</small>
              </span>
            </label>
          )}
          {!account && createAccount && (
            <>
              <label>
                <span>Password</span>
                <input
                  name="accountPassword"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  value={accountPassword}
                  onChange={(event) => setAccountPassword(event.target.value)}
                  required
                />
              </label>
              <label>
                <span>Confirm password</span>
                <input
                  name="accountPasswordConfirm"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={128}
                  value={accountPasswordConfirm}
                  onChange={(event) => setAccountPasswordConfirm(event.target.value)}
                  required
                />
              </label>
            </>
          )}
          <label className="booking-request-form__full">
            <span>Notes</span>
            <textarea name="note" maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} rows={3} />
          </label>
          <BookingNextSteps />
          <button className="button button--secondary button--full" type="submit" disabled={bookingStatus === "loading"}>
            <CalendarCheck size={18} aria-hidden="true" />
            {bookingStatus === "loading" ? "Sending request" : "Request booking"}
          </button>
        </form>
      )}

      {bookingStatus === "success" && bookingResult && (
        <div className="booking-results booking-results--success booking-results--confirmation" role="status" aria-live="polite">
          {bookingResult.account ? <ShieldCheck size={18} aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}
          <div className="booking-confirmation-copy">
            <strong>Booking request received. Reference {bookingReference}.</strong>
            <span>{bookingResult.account ? "This stay is saved to your guest account." : "We will contact you directly to confirm the stay."}</span>
            {bookingHoldExpiresText && <small>Held until {bookingHoldExpiresText} while we review your request.</small>}
            <BookingNextSteps />
            {bookingResult.account ? (
              <Link to="/account">View saved stay.</Link>
            ) : (
              <div className="booking-account-save-prompt">
                <strong>Want this request saved? </strong>
                <span>Create a guest account with this same email and this booking will appear in saved stays. </span>
                <Link
                  to="/account"
                  state={{
                    bookingAccountPrefill: {
                      name: guestName,
                      email: guestEmail,
                      phone: guestPhone,
                    },
                  }}
                >
                  Create account to save stay
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {bookingStatus === "error" && (
        <div className="booking-results booking-results--error" role="status" aria-live="polite">
          <X size={18} aria-hidden="true" />
          <span>
            {bookingMessage}
            {bookingNeedsSignIn && (
              <>
                {" "}
                <small className="booking-results__hint">The returning guest sign-in is open above. Sign in, then submit this same request again.</small>
              </>
            )}
          </span>
        </div>
      )}

      {status === "success" && result && !apartmentSlug && !availabilityModalOpen && (
        <div className="booking-results availability-reopen" role="status" aria-live="polite">
          <Check size={18} aria-hidden="true" />
          <span>
            {availableApartments.length} apartment{availableApartments.length === 1 ? "" : "s"} available for your dates.
          </span>
          <button className="booking-inline-button" type="button" onClick={() => setAvailabilityModalOpen(true)}>
            View residences <ArrowRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {!apartmentSlug &&
        typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {availabilityModalOpen && (
              <m.div
                className="availability-modal"
                key="availability-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="availability-modal-title"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.26, ease: easeOut }}
              >
                <button className="availability-modal__backdrop" type="button" onClick={() => setAvailabilityModalOpen(false)} aria-label="Close availability results" />
                <m.section
                  className="availability-modal__panel"
                  initial={{ opacity: 0, y: 26, scale: 0.985 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.99 }}
                  transition={{ duration: 0.32, ease: easeOut }}
                >
            <header className="availability-modal__header">
              <div>
                <p className="eyebrow">Availability</p>
                <h2 id="availability-modal-title">
                  {status === "loading"
                    ? "Checking your stay."
                    : result
                      ? `${availableApartments.length} residence${availableApartments.length === 1 ? "" : "s"} available.`
                      : "Choose dates to view residences."}
                </h2>
                {result && (
                  <p>
                    {formatStayDate(result.start)} to {formatStayDate(result.end)} · {result.nights} night{result.nights === 1 ? "" : "s"} · {result.guests} guest
                    {result.guests === 1 ? "" : "s"}
                  </p>
                )}
              </div>
              <button className="icon-button" type="button" onClick={() => setAvailabilityModalOpen(false)} aria-label="Close availability results">
                <X size={22} aria-hidden="true" />
              </button>
            </header>

            <form className="availability-refine-form" onSubmit={onSubmit}>
              <label>
                <span>Check-in</span>
                <input type="date" min={minDate} value={checkIn} onClick={(event) => openNativeDatePicker(event.currentTarget)} onChange={(event) => onCheckInChange(event.target.value)} required />
              </label>
              <label>
                <span>Check-out</span>
                <input type="date" min={checkoutMinDate} value={checkOut} onClick={(event) => openNativeDatePicker(event.currentTarget)} onChange={(event) => onCheckOutChange(event.target.value)} required />
              </label>
              <label>
                <span>Adults</span>
                <select value={adults} onChange={(event) => onAdultsChange(event.target.value)}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Children</span>
                <select value={children} onChange={(event) => onChildrenChange(event.target.value)}>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </label>
              <button className="button button--primary" type="submit" disabled={status === "loading"}>
                <Search size={18} aria-hidden="true" />
                {status === "loading" ? "Checking" : "Update search"}
              </button>
            </form>

            {status === "error" && (
              <div className="availability-modal__notice availability-modal__notice--error">
                <X size={18} aria-hidden="true" />
                <span>{message}</span>
              </div>
            )}

            {status === "loading" && (
              <div className="availability-modal__notice">
                <Search size={18} aria-hidden="true" />
                <span>Checking each furnished residence for your stay.</span>
              </div>
            )}

            {result && status === "success" && (
              <div className="availability-modal__grid">
                {searchedApartments.map((apartment) => {
                  const details = getApartment(apartment.slug);
                  const stayParams = new URLSearchParams({
                    start: result.start,
                    end: result.end,
                    adults,
                    children,
                  });
                  const cardContent = (
                    <>
                      <div className="availability-card__media">
                        <img src={details?.featuredImage ?? "/assets/brand/logo-square-300.jpg"} alt={details?.alt ?? apartment.title} loading="lazy" />
                        <span className={apartment.available ? "availability-card__badge availability-card__badge--available" : "availability-card__badge"}>
                          {apartment.available ? "Available" : "Unavailable"}
                        </span>
                      </div>
                      <div className="availability-card__body">
                        <div className="availability-card__meta">
                          <span>{details?.category ?? "Apartment"}</span>
                          <span>
                            <Users size={15} aria-hidden="true" />
                            Sleeps {details?.sleeps ?? apartment.maxGuests}
                          </span>
                          {details?.bedrooms !== undefined && <span>{details.bedrooms === 0 ? "Studio" : `${details.bedrooms} Bedroom${details.bedrooms === 1 ? "" : "s"}`}</span>}
                        </div>
                        <h3>{details?.detailTitle ?? apartment.title}</h3>
                        {details?.summary && <p>{details.summary}</p>}
                        <div className="availability-card__facts">
                          {details?.beds && <span>{details.beds}</span>}
                          {details?.bathrooms && <span>{details.bathrooms}</span>}
                          {details?.kitchen && <span>{details.kitchen}</span>}
                        </div>
                        <div className="availability-card__footer">
                          <span>
                            <strong>{apartment.available ? money(apartment.total) : money(apartment.priceNight)}</strong>
                            <small>{apartment.available ? "Estimated stay total" : apartment.reason ?? "Try another date"}</small>
                          </span>
                          <span className="availability-card__cta">
                            {apartment.available ? "View stay" : "Unavailable"} {apartment.available && <ArrowRight size={16} aria-hidden="true" />}
                          </span>
                        </div>
                      </div>
                    </>
                  );

                  return apartment.available ? (
                    <Link className="availability-card availability-card--suite" key={apartment.slug} to={`/apartments/${apartment.slug}?${stayParams.toString()}#booking-calendar`}>
                      {cardContent}
                    </Link>
                  ) : (
                    <article className="availability-card availability-card--suite availability-card--unavailable" key={apartment.slug} aria-disabled="true">
                      {cardContent}
                    </article>
                  );
                })}
                {searchedApartments.length === 0 && <span className="availability-drawer__empty">No apartments were returned for those dates.</span>}
              </div>
            )}

            <footer className="availability-modal__footer">
              <span>Need help choosing? Call 917-299-6441 for booking questions.</span>
              <Link className="text-link" to="/contact" onClick={() => setAvailabilityModalOpen(false)}>
                Contact us
              </Link>
            </footer>
                </m.section>
              </m.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </div>
  );
}
